/**
 * SpectrumLine (mobile RN) — one DMT's −4..+4 slider: Mala-D left / DMT right,
 * avatar at the displayed position with a confidence ring. Frozen during the
 * week; `animateFrom` animates old→new at the reveal. Mirrors the web
 * component (src/components/olmps/core/spectrum-line.tsx).
 */
import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, Text, View } from 'react-native';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../v10/theme';
import { GlassCard } from '../v10/ui';
import { POSITION_MIN, POSITION_MAX } from './spectrum';

const POINTS = [-4, -3, -2, -1, 0, 1, 2, 3, 4];

function pct(pos: number): number {
  return ((pos - POSITION_MIN) / (POSITION_MAX - POSITION_MIN)) * 100;
}

export interface SpectrumLineProps {
  toolName: string;
  maladyName: string;
  displayed: number;
  repCount: number;
  acquisition: string;
  animateFrom?: number;
  secured?: boolean;
}

export function SpectrumLine({
  toolName,
  maladyName,
  displayed,
  repCount,
  acquisition,
  animateFrom,
  secured,
}: SpectrumLineProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const anim = useRef(new Animated.Value(pct(animateFrom ?? displayed))).current;

  useEffect(() => {
    if (animateFrom === undefined || animateFrom === displayed) {
      anim.setValue(pct(displayed));
      return;
    }
    anim.setValue(pct(animateFrom));
    Animated.timing(anim, {
      toValue: pct(displayed),
      duration: 700,
      useNativeDriver: false,
    }).start();
  }, [animateFrom, displayed, anim]);

  const conf = Math.max(0, Math.min(1, repCount / 10));
  const ringWidth = 1.5 + conf * 2;
  const negative = displayed < 0;

  const avatarColor = secured ? t.gold : displayed >= 0 ? t.onPace : t.maladyRed;

  return (
    <GlassCard style={styles.card}>
      <View style={styles.labels}>
        <Text style={[styles.malady, negative && styles.maladyActive]}>{maladyName}</Text>
        <Text style={styles.tool}>{toolName}</Text>
        <Text style={[styles.dmt, displayed > 0 && styles.dmtActive]}>{toolName}</Text>
      </View>

      <View style={styles.track}>
        <View style={[styles.rail, secured && ({ boxShadow: `0 0 8px ${t.goldGlow}` } as object)]} />
        {POINTS.map((p) => (
          <View key={p} style={[styles.tick, { left: `${pct(p)}%` }]} />
        ))}
        <Animated.View
          style={[
            styles.avatar,
            {
              left: anim.interpolate({ inputRange: [0, 100], outputRange: ['0%', '100%'] }),
              backgroundColor: avatarColor,
              borderColor: t.goldBright,
              borderWidth: ringWidth,
            },
          ]}
        >
          <Text style={styles.avatarText}>{displayed > 0 ? `+${displayed}` : `${displayed}`}</Text>
        </Animated.View>
      </View>

      <Text style={styles.status}>{secured ? 'secured' : acquisition}</Text>
    </GlassCard>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    card: {
      borderRadius: 22,
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    labels: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 },
    malady: { fontSize: 11, fontWeight: '500', color: t.ivoryFaint },
    maladyActive: { color: t.maladyText },
    tool: { fontFamily: DISPLAY_FONT, fontSize: 13, letterSpacing: 1.5, color: t.ivory },
    dmt: { fontSize: 11, fontWeight: '500', color: t.ivoryFaint },
    dmtActive: { color: t.goldBright },
    track: { position: 'relative', height: 24, justifyContent: 'center' },
    rail: { position: 'absolute', left: 0, right: 0, height: 3, borderRadius: 2, backgroundColor: t.track },
    tick: { position: 'absolute', width: 6, height: 6, borderRadius: 3, backgroundColor: t.dotOff, marginLeft: -3 },
    avatar: {
      position: 'absolute',
      width: 24,
      height: 24,
      borderRadius: 12,
      marginLeft: -12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarText: { fontSize: 10, fontWeight: '800', color: t.onGold },
    status: { fontFamily: DISPLAY_FONT, marginTop: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: t.ivoryFaint, textAlign: 'right' },
  }),
);
